import { FC, useCallback, useState, Suspense } from 'react';
import { useFragment } from 'react-relay/hooks';
import { Outlet } from 'react-router-dom';
import graphql from 'babel-plugin-relay/macro';

import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import MainToolbar from 'components/MainToolbar';
import MainMenu from 'components/MainMenu';
import AuthGuard from 'components/AuthGuard';
import Login from 'components/Login';
import Notifications from 'components/Notifications';
import CurrentUserProvider from 'components/CurrentUserProvider';
import ErrorBoundary from 'components/ErrorBoundary';

import { Main_data$key } from '__generated__/Main_data.graphql';

type Props = {
  data: Main_data$key;
};

const Main: FC<Props> = props => {
  const [open, setOpen] = useState(localStorage.getItem('mainMenuOpen') ?? '1');

  const data = useFragment(
    graphql`
      fragment Main_data on Query {
        ...AuthGuard_data
        ...CurrentUserProvider_data
        ...MainToolbar_data
      }
    `,
    props.data
  );

  const toggleDrawer = useCallback(() => {
    setOpen(v => {
      const newValue = v === '1' ? '0' : '1';

      localStorage.setItem('mainMenuOpen', newValue);

      return newValue;
    });
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <Notifications>
        <AuthGuard data={data} loginComponent={<Login />}>
          <CurrentUserProvider data={data}>
            <MainToolbar
              data={data}
              open={open === '1'}
              onToggle={toggleDrawer}
            />
            <MainMenu open={open === '1'} onToggle={toggleDrawer} />

            <Box
              component="main"
              sx={{
                backgroundColor: theme =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900],
                flexGrow: 1,
                height: '100vh',
                overflow: 'auto',
              }}>
              <Toolbar />

              <Box sx={{ p: 2 }}>
                <ErrorBoundary>
                  <Suspense fallback={<div>Loading...</div>}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </Box>
            </Box>
          </CurrentUserProvider>
        </AuthGuard>
      </Notifications>
    </Box>
  );
};

export default Main;
